import { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar, viewAccentFor } from './Sidebar';
import { Topbar } from './Topbar';
import { IdleLogout } from '@/shared/components/auth/IdleLogout';
import { FeedbackButton } from '@/shared/components/feedback/FeedbackButton';
import { PolicyGate } from '@/shared/components/legal/PolicyGate';
import { GlobalToastStack } from '@/shared/components/layout/GlobalToastStack';
import { CepikAlertPopup } from '@/features/admin/cepik/CepikAlertPopup';
import { SobrietyAlertPopup } from '@/features/forms/SobrietyAlertPopup';
import { ChangelogModal } from '@/shared/components/layout/ChangelogModal';
import { ScrollToTopButton } from '@/shared/components/layout/ScrollToTopButton';
import { useUIStore } from '@store/ui.store';
import { useProfileSync } from '@/shared/hooks/useAuth';
import { cn } from '@/shared/lib/utils';

export function AppLayout() {
  const { sidebarOpen } = useUIStore();
  // Self-heal stale sessions: refresh role + permissions from /auth/profile on boot.
  useProfileSync();
  // Przewijana treść — przycisk „Na górę" słucha właśnie jej, nie okna.
  const mainRef = useRef<HTMLElement>(null);
  // Kolor widoku na <html>, żeby karty w oknach i szufladach (portale) też go dostały.
  const viewAccent = viewAccentFor(useLocation().pathname);
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--view-accent', viewAccent);
    return () => {
      root.style.removeProperty('--view-accent');
    };
  }, [viewAccent]);

  return (
    <div className="flex h-screen overflow-hidden bg-page">
      <IdleLogout />
      <FeedbackButton />
      <PolicyGate />
      <GlobalToastStack />
      {/* B-408: okno alertu o utracie uprawnień (CEPiK) — tylko dla odbiorców z ustawień */}
      <CepikAlertPopup />
      {/* B-653: okno alarmu kontroli trzeźwości (pozytywny wynik / odmowa) — tylko dla odbiorców alarmu (serwer filtruje) */}
      <SobrietyAlertPopup />
      <ChangelogModal />
      {/* Sidebar */}
      <div className={cn(
        'hidden md:flex h-full flex-col',
        'transition-all duration-200',
      )}>
        <Sidebar />
      </div>

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <Topbar />
        <main ref={mainRef} className="flex-1 overflow-y-auto">
          {/* `h-full` zostaje dla widoków na całą wysokość (mapy, planer). Dolny odstęp `p-*` kończy się jednak na
              wysokości okna, więc dłuższa treść dochodziła do samej krawędzi (lista Urządzeń, 29.10.2026) —
              pseudo-element `after:` stoi ZA treścią i daje ten sam odstęp na końcu przewijania. */}
          <div className="h-full p-4 md:p-6 after:block after:h-4 after:content-[''] md:after:h-6">
            <Outlet />
          </div>
        </main>
        <ScrollToTopButton targetRef={mainRef} />
      </div>
    </div>
  );
}
